/**
 * v3.18-1299 COMM-DIR-002 — Contact Directory CSV bulk import.
 *
 * Operators paste a CSV or upload a file, map columns to Directory
 * fields, preview the mapped rows, then commit.
 */
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Upload, FileText, CheckCircle2, AlertTriangle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import api from '../lib/api';

// Fields the operator can map INTO.
const TARGET_FIELDS = [
  { key: '__skip__',   label: '— skip this column —' },
  { key: 'full_name',  label: 'Full name' },
  { key: 'title',      label: 'Title' },
  { key: 'company',    label: 'Company (also fills department)' },
  { key: 'department', label: 'Department' },
  { key: 'phone_mobile',    label: 'Phone · mobile' },
  { key: 'phone_office',    label: 'Phone · office' },
  { key: 'phone_home',      label: 'Phone · home' },
  { key: 'phone_extension', label: 'Phone · extension' },
  { key: 'phone_whatsapp',  label: 'Phone · WhatsApp' },
  { key: 'phone_fax',       label: 'Phone · fax' },
  { key: 'email_work',      label: 'Email · work' },
  { key: 'email_personal',  label: 'Email · personal' },
  { key: 'groups',      label: 'Groups (comma-separated)' },
  { key: 'is_favorite', label: 'Favorite (yes/no)' },
  { key: 'is_primary',  label: 'Primary contact (yes/no)' },
  { key: 'client_id',   label: 'Client ID' },
  { key: 'notes',       label: 'Notes' },
];

/**
 * Very small CSV parser that handles quoted fields with embedded
 * commas / quotes / newlines. Good enough for the operator's export
 * from Excel / Numbers / Google Sheets.
 */
const parseCsv = (text) => {
  const rows = [];
  let field = '';
  let row = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') { field += '"'; i += 1; }
      else if (ch === '"') { inQuotes = false; }
      else { field += ch; }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field); field = '';
    } else if (ch === '\r') { /* skip */ }
    else if (ch === '\n') {
      row.push(field); field = '';
      if (row.some((v) => v.length > 0)) rows.push(row);
      row = [];
    } else {
      field += ch;
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    if (row.some((v) => v.length > 0)) rows.push(row);
  }
  return rows;
};

const inferMapping = (header) => {
  const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const HINTS = {
    name: 'full_name', fullname: 'full_name', firstname: 'full_name',
    lastname: 'full_name', contact: 'full_name',
    title: 'title', role: 'title', jobtitle: 'title',
    company: 'company', organization: 'company', account: 'company',
    department: 'department', team: 'department',
    mobile: 'phone_mobile', cell: 'phone_mobile', cellphone: 'phone_mobile',
    phone: 'phone_mobile', phonenumber: 'phone_mobile',
    office: 'phone_office', work: 'phone_office', workphone: 'phone_office',
    home: 'phone_home', homephone: 'phone_home',
    ext: 'phone_extension', extension: 'phone_extension',
    whatsapp: 'phone_whatsapp',
    fax: 'phone_fax',
    email: 'email_work', workemail: 'email_work',
    personalemail: 'email_personal',
    groups: 'groups', tag: 'groups', tags: 'groups', list: 'groups',
    favorite: 'is_favorite', star: 'is_favorite',
    primary: 'is_primary',
    clientid: 'client_id', client: 'client_id',
    notes: 'notes', comment: 'notes', comments: 'notes',
  };
  return header.map((h) => HINTS[norm(h)] || '__skip__');
};

const truthy = (v) => {
  const s = String(v || '').trim().toLowerCase();
  return ['1', 'y', 'yes', 'true', 't', 'x', '✓'].includes(s);
};

const mapRow = (header, mapping, cells) => {
  const out = {
    full_name: '', title: '', company: '', department: '', notes: '',
    client_id: null, is_favorite: false, is_primary: false,
    phones: [], emails: [], group_names: [],
  };
  header.forEach((_h, i) => {
    const target = mapping[i];
    const value = (cells[i] || '').trim();
    if (!value || target === '__skip__') return;
    if (target === 'groups') {
      out.group_names.push(...value.split(/[;,]/).map((g) => g.trim()).filter(Boolean));
    } else if (target === 'is_favorite') {
      out.is_favorite = truthy(value);
    } else if (target === 'is_primary') {
      out.is_primary = truthy(value);
    } else if (target?.startsWith('phone_')) {
      out.phones.push({ label: target.replace('phone_', ''), number: value });
    } else if (target?.startsWith('email_')) {
      out.emails.push({ label: target.replace('email_', ''), address: value });
    } else if (target in out) {
      out[target] = value;
    }
  });
  if (out.phones[0]) out.phones[0].is_primary = true;
  if (out.emails[0]) out.emails[0].is_primary = true;
  return out;
};

const ContactImportDialog = ({ open, onOpenChange, companyId, onDone }) => {
  const [rawText, setRawText] = useState('');
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [summary, setSummary] = useState(null);

  const header = rows[0] || [];
  const dataRows = rows.slice(1);

  const applyText = (text) => {
    setRawText(text);
    const parsed = parseCsv(text);
    setRows(parsed);
    setMapping(parsed.length ? inferMapping(parsed[0]) : []);
    setSummary(null);
  };

  const handleFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    applyText(text);
  };

  const mapped = useMemo(() => {
    if (!dataRows.length || !header.length) return [];
    return dataRows.map((cells) => mapRow(header, mapping, cells));
  }, [header, mapping, dataRows]);

  const submit = async (dryRun) => {
    if (!companyId) { toast.error('Pick a company first.'); return; }
    if (!mapped.length) { toast.error('Paste CSV data before importing.'); return; }
    if (!mapped.every((r) => (r.full_name || '').trim())) {
      toast.error('Every row must have a Full Name (map at least one column).');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post('/communication-contacts/bulk-import', {
        company_id: companyId,
        rows: mapped,
        dry_run: !!dryRun,
      });
      setSummary(data);
      if (!dryRun) {
        toast.success(`Imported ${data.created} contacts`);
        onDone?.();
      } else {
        toast.info(`Preview OK — ${data.processed} rows ready, ${data.errors.length} errors`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="contact-import-dialog"
      >
        <DialogHeader>
          <DialogTitle>Import contacts from CSV</DialogTitle>
          <DialogDescription>
            Paste CSV text or upload a file. We map columns automatically —
            override any header below, run a dry-run, then commit.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">CSV file</Label>
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept=".csv,text/csv,text/plain"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="contact-import-file"
                className="text-xs"
              />
              <span className="text-xs text-slate-500">or paste below</span>
            </div>
          </div>
          <div>
            <Label className="text-xs">Or paste CSV text</Label>
            <Textarea
              rows={5}
              value={rawText}
              onChange={(e) => applyText(e.target.value)}
              placeholder="Name,Company,Phone,Email,Groups"
              data-testid="contact-import-text"
              className="font-mono text-xs"
            />
          </div>

          {header.length > 0 && (
            <div className="border rounded-md">
              <div className="px-2 py-1.5 border-b text-[11px] font-semibold uppercase tracking-wider text-slate-600 bg-slate-50">
                Column mapping · {dataRows.length} data rows
              </div>
              <div className="grid grid-cols-3 gap-2 p-2">
                {header.map((h, i) => (
                  <div key={`${h}-${i}`} className="flex flex-col gap-0.5">
                    <span className="text-[10px] uppercase text-slate-400 truncate">
                      Column {i + 1}: {h}
                    </span>
                    <Select
                      value={mapping[i] || '__skip__'}
                      onValueChange={(v) => setMapping((m) => {
                        const next = [...m]; next[i] = v; return next;
                      })}
                    >
                      <SelectTrigger data-testid={`contact-import-map-${i}`} className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TARGET_FIELDS.map((tf) => (
                          <SelectItem key={tf.key} value={tf.key} className="text-xs">{tf.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {mapped.length > 0 && (
            <div className="border rounded-md">
              <button
                type="button"
                onClick={() => setPreviewOpen((o) => !o)}
                className="w-full text-left px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600 hover:bg-slate-50"
              >
                {previewOpen ? '▼' : '▶'} Preview — first {Math.min(mapped.length, 5)} of {mapped.length} rows
              </button>
              {previewOpen && (
                <div className="divide-y text-xs max-h-56 overflow-y-auto">
                  {mapped.slice(0, 5).map((r, i) => (
                    <div key={i} className="px-2 py-1.5" data-testid={`contact-import-preview-${i}`}>
                      <div className="font-medium text-slate-800">{r.full_name || <em className="text-red-500">Missing name</em>}</div>
                      <div className="text-slate-500 flex flex-wrap gap-x-2 gap-y-0.5">
                        {r.title && <span>{r.title}</span>}
                        {r.company && <span>· {r.company}</span>}
                        {r.phones.map((p) => <span key={p.number}>{p.label}: {p.number}</span>)}
                        {r.emails.map((e) => <span key={e.address}>{e.label}: {e.address}</span>)}
                        {r.group_names.length > 0 && <span>· groups: {r.group_names.join(', ')}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {summary && (
            <div className={`border rounded-md p-2 text-xs ${summary.errors?.length ? 'border-amber-300 bg-amber-50' : 'border-emerald-300 bg-emerald-50'}`}>
              <div className="font-semibold flex items-center gap-1">
                {summary.errors?.length ? (
                  <AlertTriangle className="h-3 w-3 text-amber-600" />
                ) : (
                  <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                )}
                {summary.dry_run ? 'Dry-run result' : 'Import complete'}
              </div>
              <div className="mt-1 text-slate-700">
                {summary.processed} rows processed · {summary.created} created
                {summary.errors?.length ? ` · ${summary.errors.length} errors` : ''}
                {summary.groups_created?.length ? ` · created groups: ${summary.groups_created.join(', ')}` : ''}
              </div>
              {summary.errors?.slice(0, 5).map((e, i) => (
                <div key={i} className="text-[10px] text-red-700">
                  Row {e.row + 1}: {e.error}
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            variant="outline"
            onClick={() => submit(true)}
            disabled={submitting || !mapped.length}
            data-testid="contact-import-dry-run"
          >
            <FileText className="h-3 w-3 mr-1" /> Dry-run
          </Button>
          <Button
            onClick={() => submit(false)}
            disabled={submitting || !mapped.length}
            data-testid="contact-import-commit"
          >
            <Upload className="h-3 w-3 mr-1" />
            {submitting ? 'Importing…' : 'Import contacts'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContactImportDialog;
